import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";
import { scrollToId } from "@/App";

const HERO_IMG =
  "https://images.unsplash.com/photo-1607300110506-273ab1cf41f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const LINES = ["Premier B2B Textile", "& Garment Trading", "House in Dubai"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="kinetic-hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="Draped silk fabric" className="w-full h-[115%] object-cover object-center" />
        <div className="absolute inset-0 bg-black/55" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at 50% -20%, rgba(212,175,55,0.18), transparent 70%)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-[#0A0B0E]/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 pt-40 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#0A0B0E]/50 backdrop-blur-md px-4 py-2 mb-10"
          data-testid="hero-location-badge"
        >
          <MapPin size={13} className="text-[#D4AF37]" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-[#F3E5AB]/90">Ontario Tower · Business Bay · Dubai</span>
        </motion.div>

        <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[1.04] tracking-tight text-[#F8F5EE]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-gold-gradient" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.16, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`hero-headline-line-${i + 1}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-8 max-w-xl text-base sm:text-lg font-light leading-relaxed text-slate-300"
          data-testid="hero-subcopy"
        >
          Premium women's fabrics — silks, chiffons, georgettes, cottons and embroidered materials — along with
          garments, Arabic wear, sportswear, accessories, perfumes and footwear, supplied from Dubai to UAE
          retailers, GCC businesses and regional distributors.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-explore-catalog-button"
            onClick={() => scrollToId("#categories")}
            className="group px-8 py-4 bg-[#D4AF37] text-[#0A0B0E] text-xs font-semibold tracking-[0.22em] uppercase hover:bg-[#E5C158] transition-colors duration-300 flex items-center gap-2"
          >
            Explore Wholesale Catalog
            <ArrowDownRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-request-rfq-button"
            onClick={() => scrollToId("#contact")}
            className="px-8 py-4 border border-[#F8F5EE]/30 text-[#F8F5EE] text-xs tracking-[0.22em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
          >
            Request Bulk RFQ
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
